import { TableauLogoColorIcon } from '@tableau/icons/TableauLogoColorIcon';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { ILogo, LogoService } from '../../../settings/general-settings/components/sections/siteLogo/services/LogoService';
import { Logo } from '../header/Logo';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { NavStyles } from './NavigationStyles';
import { LogoWrapperDivTestId } from './NavPanel';

// Basename of the built-in default Tableau logo mark (see LogoService
// getDefaultHeaderLogo / getDefaultCompactLogo). Its imgPath() also produces a
// dark-theme variant (Tableau_Logo_Mark_tds-dark.svg) and a BuildId query
// string, so we match on the stable basename substring rather than the exact
// URL. No custom or preview logo image contains this basename.
const DEFAULT_LOGO_MARK = 'Tableau_Logo_Mark';

export interface NavPanelLogoProps {
  logo: ILogo;
  compactLogo: ILogo;
  siteUrls: SpacesSiteUrls;
  collapsed: boolean;
  onClick?: () => void;
  disableLink?: boolean;
}

export const NavPanelLogo: React.SFC<NavPanelLogoProps> = (props: NavPanelLogoProps) => {
  const displayLogo = props.collapsed ? props.compactLogo : props.logo;
  displayLogo.styleOverrides = NavStyles.LogoImage;
  displayLogo.title = Messages.navigateToStartPage();

  // Migrate ONLY the built-in default Tableau logo to an inline @tableau/icons
  // glyph. A custom site/server logo keeps its legacy CSS-background render.
  //
  // Discriminator: getNavPanelLogo() returns the default IFF !hasCustomHeaderLogo();
  // getCompactLogo() returns the default IFF (!hasCustomCompactLogo() && !hasCustomHeaderLogo())
  // -- both terms are required for the compact case because getCompactLogo() also
  // returns a resized custom header logo when only a header custom logo is set.
  //
  // The extra displayLogo.image guard keeps the shared component correct for the
  // UpdateSiteLogoForm live preview, where the passed logo can be an unsaved
  // preview (a custom file) while LogoService still reports the saved default
  // state -- without the guard the fixed icon would paint over that preview.
  const savedDefault = props.collapsed
    ? (!LogoService.hasCustomCompactLogo() && !LogoService.hasCustomHeaderLogo())
    : !LogoService.hasCustomHeaderLogo();
  const isDefaultLogo = savedDefault && displayLogo.image.indexOf(DEFAULT_LOGO_MARK) !== -1;

  const iconOverride = isDefaultLogo
    ? <TableauLogoColorIcon size={props.collapsed ? 24 : 36} />
    : undefined;

  // we want the panel to not shift when expanding/collapsing, so we wrap a div fixed to the larger height of
  // the two logos so the logo area always stays the same.
  const biggerHeight = Math.max(props.logo.height, props.compactLogo.height);
  const wrappingDivStyle = {
    height: biggerHeight,
    marginTop: NavStyles.LogoMarginTop,
    marginBottom: NavStyles.LogoMarginBottom,
    ...CommonStyles.Layout.FlexBlockVerticalCenter
  };

  return (
    <div data-tb-test-id={LogoWrapperDivTestId} style={wrappingDivStyle}>
      <Logo siteUrls={props.siteUrls} logo={displayLogo} onClick={props.onClick} disableLink={props.disableLink} iconOverride={iconOverride}/>
    </div>
  );
};
